import { Box, Button, Group, Modal, Stack, TagsInput, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PromptTemplateContentField } from "src/components/promptTemplateContentField";
import { useUpsertPromptTemplate } from "src/hooks/usePromptTemplates";

type PromptTemplateCreateModalProps = {
    opened: boolean;
    onClose: () => void;
    onCreated?: (id: string) => void;
};

export function PromptTemplateCreateModal({
    opened,
    onClose,
    onCreated,
}: PromptTemplateCreateModalProps) {
    const queryClient = useQueryClient();
    const upsertPromptTemplate = useUpsertPromptTemplate();
    const [isSaving, setIsSaving] = useState(false);

    const form = useForm({
        initialValues: {
            title: "",
            persona: "",
            content: "",
            tags: [] as string[],
        },
        validate: {
            title: (value) => (!value ? "Title is required" : null),
            content: (value) => (!value ? "Content is required" : null),
        },
    });

    const handleClose = () => {
        form.reset();
        onClose();
    };

    const handleSave = async (values: typeof form.values) => {
        setIsSaving(true);
        try {
            const result = await upsertPromptTemplate({
                ...values,
                is_admin_provided: false,
            });

            if (!result) throw new Error("Failed to save prompt template");

            notifications.show({
                title: "Success",
                message: "Prompt template created successfully",
                color: "green",
            });

            // The backend is eventually consistent (the admin manage view waits before
            // refetching too); give it a moment before invalidating so the new template is
            // included in the refetched list.
            await new Promise((resolve) => setTimeout(resolve, 1000));
            await queryClient.invalidateQueries({ queryKey: ["prompt-templates"] });
            form.reset();
            onCreated?.(result.id);
            onClose();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to save prompt template",
                color: "red",
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Create Prompt Template"
            size="lg"
            closeButtonProps={{ "aria-label": "Close modal" }}
        >
            <Box
                component="form"
                onSubmit={(event) => {
                    // The picker/create modal is portaled but still nested inside the chat
                    // composer's <form> in the React tree. React bubbles submit events through
                    // the React tree, so without stopping propagation this submit would also
                    // trigger the chat form's onSubmit (and fire the chat completion API).
                    event.stopPropagation();
                    form.onSubmit(handleSave)(event);
                }}
            >
                <Stack gap="md">
                    <TextInput
                        required
                        label="Title"
                        placeholder="e.g., Incident Summary"
                        {...form.getInputProps("title")}
                    />
                    <TextInput
                        label="Persona"
                        placeholder="Optional persona for this template"
                        {...form.getInputProps("persona")}
                    />
                    <TagsInput
                        label="Tags"
                        placeholder="Type a tag and press Enter"
                        {...form.getInputProps("tags")}
                    />
                    <PromptTemplateContentField
                        required
                        value={form.values.content}
                        onChange={(value) => form.setFieldValue("content", value)}
                        error={form.errors.content}
                    />
                    <Group justify="flex-end" mt="md">
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" color="green" loading={isSaving}>
                            Create Template
                        </Button>
                    </Group>
                </Stack>
            </Box>
        </Modal>
    );
}
